import { Box, ButtonGroup, Fab, FormGroup, Grid, IconButton, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material"
import { AccountUser, CreateAccountUserRequest } from "../../apis/ersys-data/api"
import { useEffect, useState } from "react"
import { useErsysDataApi } from "../ErsysDataApiProvider"
import { showError } from "../../features/errors/Errors"
import { setIsLoading } from "../../features/menus/AppMenu"
import { useDispatch } from "react-redux"
import { useParams } from "react-router-dom"
import { AddSharp, DeleteSharp, KeySharp } from "@mui/icons-material"
import AccountUserCreateModal from "./modals/AccountUserCreateModal"

interface AccountUsersTabProps {
    formSpacing: number
}

const AccountUsersTab: React.FC<AccountUsersTabProps> = () => {
    const ersysDataApiServices = useErsysDataApi()
    const [users, setUsers] = useState<AccountUser[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [loaded, setLoaded] = useState<boolean>(false)
    const [isCreateVisible, setIsCreateVisible] = useState<boolean>(false)
    const dispatch = useDispatch()
    const params = useParams()

    useEffect(() => {
        dispatch(setIsLoading(loading))
    }, [loading, dispatch])

    useEffect(() => {
        dispatch(setIsLoading(true))
        const fetch = async () => {
            try {
                const accountId = parseInt(params.id!, 10)
                var accountPlansResp = await ersysDataApiServices.accountsService.accountsListUsers(accountId)
                setUsers(accountPlansResp.data.users)
            }
            catch (ex: any) {
                dispatch(
                    showError({
                        title: "Unexpected Error occurred",
                        message:
                            "An unexpected error occurred while getting user data. If problem persists please contact IT.",
                    }),
                )
                console.log(ex)
            } finally {
                setLoaded(true)
                dispatch(setIsLoading(false))
            }
        }
        fetch()
    }, [dispatch, setUsers, ersysDataApiServices.accountsService, params.id])

    const showSkeleton = () => {
        return users.length < 1 && !loaded
    }

    const renderSkeleton = () => {
        return (
            <FormGroup>
                {[...Array(5)].map((_, i) => (
                    <Skeleton variant='rectangular' width={200} height={100} key={`skele-${i}`} sx={{ margin: 1 }} />
                ))}
            </FormGroup>
        )
    }

    const reloadUsers = async () => {
        setLoading(true)
        try {
            const accountId = parseInt(params.id!, 10)
            var accountPlansResp = await ersysDataApiServices.accountsService.accountsListUsers(accountId)
            setUsers(accountPlansResp.data.users)
        }
        catch (ex: any) {
            dispatch(
                showError({
                    title: "Unexpected Error occurred",
                    message:
                        "An unexpected error occurred while getting users. If problem persists please contact IT.",
                }),
            )
            console.log(ex)
        } finally {
            setLoading(false)
        }

    }

    const resetPassword = async (email: string) => {
        setLoading(true)
        try {
            const accountId = parseInt(params.id!, 10)
            await ersysDataApiServices.accountsService.accountsResetPassword(accountId, email)
        }
        catch (ex: any) {
            dispatch(
                showError({
                    title: "Unexpected Error occurred",
                    message:
                        "An unexpected error occurred while resetting password. If problem persists please contact IT.",
                }),
            )
            console.log(ex)
            setLoading(false)
        } finally {
            setLoading(false)
        }
    }

    const unlinkAccount = async (email: string) => {
        setLoading(true)
        try {
            const accountId = parseInt(params.id!, 10)
            await ersysDataApiServices.accountsService.accountsRemoveUser(accountId, email)
            await reloadUsers()
        }
        catch (ex: any) {
            dispatch(
                showError({
                    title: "Unexpected Error occurred",
                    message:
                        "An unexpected error occurred while unlinking user. If problem persists please contact IT.",
                }),
            )
            console.log(ex)
        } finally {
            setLoading(false)
        }
    }

    const createUser = async (req: CreateAccountUserRequest) => {
        setLoading(true)
        setIsCreateVisible(false)
        try {
            const accountId = parseInt(params.id!, 10)
            await ersysDataApiServices.accountsService.accountsAddUser(accountId, req)
            await reloadUsers()
        }
        catch (ex: any) {
            dispatch(
                showError({
                    title: "Unexpected Error occurred",
                    message:
                        "An unexpected error occurred while adding user. If problem persists please contact IT.",
                }),
            )
            console.log(ex)
            setLoading(false)
        }
    }

    return (
        <Box>
            <AccountUserCreateModal  onClose={() => setIsCreateVisible(false)} open={isCreateVisible} onCreate={createUser}/>
            {showSkeleton() ? renderSkeleton() :
                <Grid container alignItems="center" justifyContent="center">
                    <Tooltip title="Add Bag">
                        <Fab
                            onClick={() => setIsCreateVisible(true)}
                            color="primary"
                            aria-label="add"
                            sx={{ position: 'absolute', bottom: 25, right: 25 }}>
                            <AddSharp />
                        </Fab>
                    </Tooltip>
                    <TableContainer sx={{ minWidth: 650, maxHeight: 550 }} component={Paper}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Email</TableCell>
                                    <TableCell align="right">
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map((email) => (
                                    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }} key={email.email}>
                                        <TableCell>
                                            <Typography>{email.email}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <ButtonGroup>
                                                <Tooltip title="Reset Password">
                                                    <IconButton onClick={() => resetPassword(email.email)}>
                                                        <KeySharp></KeySharp>
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Remove Account">
                                                    <IconButton onClick={() => unlinkAccount(email.email)}>
                                                        <DeleteSharp></DeleteSharp>
                                                    </IconButton>
                                                </Tooltip>
                                            </ButtonGroup>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            }
        </Box>
    )
}

export default AccountUsersTab